import React, { useState, useEffect } from 'react';
import { Card, CardContent, Button, Typography, Box, Alert, Chip, TextField, CircularProgress } from '@mui/material';
import { createAuthenticatedEventSource } from '../utils/AuthenticatedEventSource';
import { isNotificationSupported, getNotificationPermission, requestNotificationPermission } from '../utils/notificationUtils';

interface DiagnosticInfo {
  serviceWorker: {
    registered: boolean;
    scope?: string;
    state?: string;
    error?: string;
  };
  sse: {
    supported: boolean;
    testResult?: string;
    error?: string;
  };
  notifications: {
    permission: string;
    supported: boolean;
  };
  browser: {
    userAgent: string;
    platform: string;
    language: string;
    onLine: boolean;
  };
  connection: {
    apiUrl: string;
    tunnelDetected: boolean;
    protocol: string;
    hostname: string;
  };
}

export const DiagnosticsPage: React.FC = () => {
  const [diagnostics, setDiagnostics] = useState<DiagnosticInfo | null>(null);
  const [testResults, setTestResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [customMessage, setCustomMessage] = useState('Test notification from diagnostics');

  useEffect(() => {
    runDiagnostics();
  }, []);

  const addTestResult = (result: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setTestResults(prev => [`[${timestamp}] ${result}`, ...prev].slice(0, 20));
  };

  const runDiagnostics = async () => {
    setLoading(true);
    const info: DiagnosticInfo = {
      serviceWorker: {
        registered: false,
        supported: 'serviceWorker' in navigator
      },
      sse: {
        supported: typeof EventSource !== 'undefined'
      },
      notifications: {
        permission: getNotificationPermission() as string,
        supported: isNotificationSupported()
      },
      browser: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        onLine: navigator.onLine
      },
      connection: {
        apiUrl: import.meta.env.VITE_API_BASE_URL || window.location.origin,
        tunnelDetected: window.location.hostname.includes('loca.lt') ||
                       window.location.hostname.includes('tunnel'),
        protocol: window.location.protocol,
        hostname: window.location.hostname
      }
    };

    // Check service worker
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length > 0) {
          const reg = registrations[0];
          info.serviceWorker = {
            registered: true,
            scope: reg.scope,
            state: reg.active?.state || 'unknown'
          };
        }
      } catch (error) {
        info.serviceWorker.error = error instanceof Error ? error.message : 'Unknown error';
      }
    }

    setDiagnostics(info);
    setLoading(false);
  };

  const testServiceWorker = async () => {
    addTestResult('Testing service worker communication...');
    
    if (!navigator.serviceWorker?.controller) {
      addTestResult('❌ No active service worker controller');
      return;
    }

    try {
      const messageChannel = new MessageChannel();
      
      const promise = new Promise((resolve) => {
        messageChannel.port1.onmessage = (event) => {
          addTestResult(`✅ Service worker responded: ${JSON.stringify(event.data)}`);
          resolve(event.data);
        };
      });

      navigator.serviceWorker.controller.postMessage(
        { type: 'CHECK_SW_STATUS' },
        [messageChannel.port2]
      );

      await Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject('Timeout'), 5000))
      ]);
    } catch (error) {
      addTestResult(`❌ Service worker test failed: ${error}`);
    }
  };

  const testSSEConnection = async () => {
    addTestResult('Testing SSE connection...');
    
    const token = localStorage.getItem('token');
    if (!token) {
      addTestResult('❌ No auth token found');
      return;
    }

    let messageCount = 0;
    const startTime = Date.now();

    try {
      // Test SSE endpoint - use a test endpoint or the existing table stream
      const testUrl = `${import.meta.env.VITE_API_BASE_URL || ''}/api/test/sse`;
      
      createAuthenticatedEventSource(
        testUrl,
        token,
        (event) => {
          messageCount++;
          addTestResult(`✅ SSE message ${messageCount}: ${JSON.stringify(event.data)}`);
          
          // Stop after 5 messages or 10 seconds
          if (messageCount >= 5 || Date.now() - startTime > 10000) {
            addTestResult('✅ SSE test completed successfully');
          }
        },
        (error) => {
          addTestResult(`❌ SSE error: ${error.message}`);
        }
      );

      // Set timeout
      setTimeout(() => {
        if (messageCount === 0) {
          addTestResult('⚠️ No SSE messages received after 10 seconds');
        }
      }, 10000);
    } catch (error) {
      addTestResult(`❌ SSE test failed: ${error.message}`);
    }
  };

  const testDirectNotification = async () => {
    addTestResult('Testing direct browser notification...');
    
    if (!isNotificationSupported()) {
      addTestResult('❌ Browser does not support notifications');
      return;
    }

    const permission = getNotificationPermission();
    
    if (permission === 'granted') {
      try {
        const notification = new Notification('SOG Diagnostics Test', {
          body: customMessage,
          icon: '/android-chrome-192x192.png',
          badge: '/favicon-32x32.png',
          vibrate: [200, 100, 200]
        });

        notification.onclick = () => {
          addTestResult('✅ Notification clicked');
          notification.close();
        };

        addTestResult('✅ Direct notification displayed');
      } catch (error) {
        addTestResult(`❌ Failed to create notification: ${error}`);
      }
    } else if (permission === 'denied') {
      addTestResult('❌ Notification permission denied');
    } else if (permission === 'not-supported') {
      addTestResult('❌ Notifications not supported on this device');
    } else {
      const newPermission = await requestNotificationPermission();
      addTestResult(`Notification permission: ${newPermission}`);
      if (newPermission === 'granted') {
        testDirectNotification();
      }
    }
  };

  const copyDiagnostics = () => {
    const data = {
      diagnostics,
      testResults,
      timestamp: new Date().toISOString()
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    addTestResult('✅ Diagnostics copied to clipboard');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        System Diagnostics
      </Typography>

      {/* Connection Info */}
      <Card sx={{ mb: { xs: 2, md: 3 }, border: '1px solid var(--border-subtle)', borderRadius: 2, boxShadow: 'none' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Connection Information</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip 
              label={`Protocol: ${diagnostics?.connection.protocol}`} 
              color={diagnostics?.connection.protocol === 'https:' ? 'success' : 'warning'}
            />
            <Chip label={`Host: ${diagnostics?.connection.hostname}`} />
            <Chip 
              label={diagnostics?.connection.tunnelDetected ? 'Tunnel Detected' : 'Direct Connection'} 
              color={diagnostics?.connection.tunnelDetected ? 'info' : 'default'}
            />
            <Chip 
              label={diagnostics?.browser.onLine ? 'Online' : 'Offline'} 
              color={diagnostics?.browser.onLine ? 'success' : 'error'}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            API URL: {diagnostics?.connection.apiUrl}
          </Typography>
        </CardContent>
      </Card>

      {/* Service Worker Status */}
      <Card sx={{ mb: { xs: 2, md: 3 }, border: '1px solid var(--border-subtle)', borderRadius: 2, boxShadow: 'none' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Service Worker</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip 
              label={diagnostics?.serviceWorker.registered ? 'Registered' : 'Not Registered'} 
              color={diagnostics?.serviceWorker.registered ? 'success' : 'error'}
            />
            {diagnostics?.serviceWorker.state && (
              <Chip label={`State: ${diagnostics.serviceWorker.state}`} />
            )}
          </Box>
          {diagnostics?.serviceWorker.scope && (
            <Typography variant="body2" color="text.secondary">
              Scope: {diagnostics.serviceWorker.scope}
            </Typography>
          )}
          {diagnostics?.serviceWorker.error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {diagnostics.serviceWorker.error}
            </Alert>
          )}
          <Button 
            variant="contained" 
            onClick={testServiceWorker} 
            sx={{
              mt: 2,
              width: { xs: '100%', sm: 'auto' },
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1.25,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%) !important',
              color: 'var(--text-inverse) !important',
              boxShadow: '0 1px 2px rgba(96, 165, 250, 0.25) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important',
                boxShadow: '0 2px 4px rgba(96, 165, 250, 0.35) !important',
              }
            }}
            disabled={!diagnostics?.serviceWorker.registered}
          >
            Test Service Worker
          </Button>
        </CardContent>
      </Card>

      {/* Browser Notifications */}
      <Card sx={{ mb: { xs: 2, md: 3 }, border: '1px solid var(--border-subtle)', borderRadius: 2, boxShadow: 'none' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Browser Notifications</Typography>
          <TextField
            fullWidth
            label="Test Message"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip 
              label={`Permission: ${diagnostics?.notifications.permission}`} 
              color={diagnostics?.notifications.permission === 'granted' ? 'success' : 'warning'}
            />
            <Chip 
              label={diagnostics?.notifications.supported ? 'Supported' : 'Not Supported'} 
              color={diagnostics?.notifications.supported ? 'success' : 'error'}
            />
          </Box>
          <Button 
            variant="contained" 
            onClick={testDirectNotification} 
            disabled={!diagnostics?.notifications.supported}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1.25,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%) !important',
              color: 'var(--text-inverse) !important',
              boxShadow: '0 1px 2px rgba(96, 165, 250, 0.25) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important',
                boxShadow: '0 2px 4px rgba(96, 165, 250, 0.35) !important',
              }
            }}
          >
            Test Direct Notification
          </Button>
        </CardContent>
      </Card>

      {/* SSE Support */}
      <Card sx={{ mb: { xs: 2, md: 3 }, border: '1px solid var(--border-subtle)', borderRadius: 2, boxShadow: 'none' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Server-Sent Events (SSE)</Typography>
          <Chip 
            label={diagnostics?.sse.supported ? 'Supported' : 'Not Supported'} 
            color={diagnostics?.sse.supported ? 'success' : 'error'}
          />
          <Button 
            variant="contained" 
            onClick={testSSEConnection} 
            sx={{
              mt: 2,
              width: { xs: '100%', sm: 'auto' },
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1.25,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%) !important',
              color: 'var(--text-inverse) !important',
              boxShadow: '0 1px 2px rgba(96, 165, 250, 0.25) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important',
                boxShadow: '0 2px 4px rgba(96, 165, 250, 0.35) !important',
              }
            }}
            disabled={!diagnostics?.sse.supported}
          >
            Test SSE Connection
          </Button>
        </CardContent>
      </Card>

      {/* Browser Info */}
      <Card sx={{ mb: { xs: 2, md: 3 }, border: '1px solid var(--border-subtle)', borderRadius: 2, boxShadow: 'none' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Browser Information</Typography>
          <Typography variant="body2" color="text.secondary">
            Platform: {diagnostics?.browser.platform}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Language: {diagnostics?.browser.language}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ 
            fontSize: '0.75rem',
            mt: 1,
            wordBreak: 'break-word'
          }}>
            User Agent: {diagnostics?.browser.userAgent}
          </Typography>
        </CardContent>
      </Card>

      {/* Test Results */}
      {testResults.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Test Results</Typography>
              <Box>
                <Button onClick={() => setTestResults([])} sx={{ mr: 1 }}>
                  Clear
                </Button>
                <Button onClick={copyDiagnostics} variant="outlined">
                  Copy All
                </Button>
              </Box>
            </Box>
            <Box sx={{ 
              bgcolor: 'grey.900', 
              color: 'common.white',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              maxHeight: 400,
              overflow: 'auto'
            }}>
              {testResults.map((result, index) => (
                <div key={index}>{result}</div>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};